/**
 * IoT Health Monitor — Main Tab Component
 * Displays fleet health overview grid with equipment cards, KPIs, and alerts.
 * Includes browser push notifications for critical threshold breaches.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Activity, Wifi, WifiOff, AlertTriangle, Shield, Wrench,
  ChevronRight, RefreshCw, Zap, Play, Square, BellRing, X, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import EquipmentDetailPanel from './EquipmentDetailPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const HEALTH_COLOR = (score) => score >= 80 ? '#22C55E' : score >= 60 ? '#F59E0B' : '#EF4444';
const STATUS_STYLES = {
  ONLINE: { color: '#22C55E', bg: '#22C55E15', label: 'Online' },
  OFFLINE: { color: '#6B7280', bg: '#6B728015', label: 'Offline' },
  ALERT: { color: '#EF4444', bg: '#EF444415', label: 'Alert' },
};

// ── Browser Notification helpers ──
function requestNotifPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}
function sendBrowserNotif(title, body, tag) {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      new Notification(title, { body, icon: '/favicon.ico', tag, requireInteraction: true });
    } catch {}
  }
}

function HealthGauge({ score, size = 48 }) {
  const color = HEALTH_COLOR(score);
  const pct = Math.max(0, Math.min(100, score));
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border-color)" strokeWidth={3} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={3}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold" style={{ color }}>{Math.round(pct)}</span>
      </div>
    </div>
  );
}

function MiniSparkline({ data, width = 80, height = 24, color = '#3B82F6' }) {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(' ');
  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
}

function FleetKPIStrip({ summary }) {
  const kpis = [
    { label: 'Fleet Health', value: summary.fleetHealthScore || 0, suffix: '/100', color: HEALTH_COLOR(summary.fleetHealthScore || 0), icon: Shield },
    { label: 'Units Online', value: `${summary.unitsOnline || 0}/${summary.totalUnits || 0}`, color: '#3B82F6', icon: Wifi },
    { label: 'Active Alerts', value: summary.activeAlerts || 0, color: summary.criticalAlerts > 0 ? '#EF4444' : '#F59E0B', icon: AlertTriangle },
    { label: 'Predicted Failures (30d)', value: summary.predictedFailures30d || 0, color: '#143A6B', icon: Zap },
    { label: 'Maintenance Due (7d)', value: summary.maintenanceDue7d || 0, color: '#0EA5E9', icon: Wrench },
  ];
  return (
    <div className="grid grid-cols-5 gap-3">
      {kpis.map((k, i) => (
        <div key={i} className="rounded-xl p-3" data-testid={`iot-kpi-${i}`}
          style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center justify-between mb-1">
            <div className="p-1.5 rounded-lg" style={{ background: `${k.color}12`, border: `1px solid ${k.color}20` }}>
              <k.icon className="w-3.5 h-3.5" style={{ color: k.color }} />
            </div>
          </div>
          <div className="text-xl font-bold" style={{ color: k.color }}>
            {typeof k.value === 'number' ? Math.round(k.value) : k.value}{k.suffix || ''}
          </div>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{k.label}</div>
        </div>
      ))}
    </div>
  );
}

function EquipmentCard({ device, onClick }) {
  const st = STATUS_STYLES[device.status] || STATUS_STYLES.ONLINE;
  const healthColor = HEALTH_COLOR(device.healthScore);
  return (
    <div onClick={onClick}
      className="rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.01]"
      style={{ backgroundColor: 'var(--card-bg)', border: `1px solid ${device.status === 'ALERT' ? '#EF444440' : 'var(--border-color)'}` }}
      data-testid={`iot-device-card-${device.equipmentCode}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono" style={{ color: 'var(--text-disabled)' }}>{device.equipmentCode}</div>
          <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{device.equipmentName}</div>
        </div>
        <HealthGauge score={device.healthScore} size={44} />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: st.bg, color: st.color }}>
          {device.status === 'ONLINE' ? <Wifi className="w-3 h-3 inline mr-0.5" /> : device.status === 'ALERT' ? <AlertTriangle className="w-3 h-3 inline mr-0.5" /> : <WifiOff className="w-3 h-3 inline mr-0.5" />}
          {st.label}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)' }}>
          {device.category?.replace(/_/g, ' ')}
        </span>
        {device.alertCount > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#EF444415', color: '#EF4444' }}>
            {device.alertCount} alert{device.alertCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="space-y-1.5 mb-3">
        {(device.topParams || []).map((p, i) => (
          <div key={i} className="flex items-center justify-between text-[11px]">
            <span style={{ color: 'var(--text-muted)' }}>{p.name}</span>
            <span className="font-mono font-medium" style={{ color: p.status === 'CRITICAL' ? '#EF4444' : p.status === 'WARNING' ? '#F59E0B' : 'var(--text-primary)' }}>
              {typeof p.value === 'number' ? p.value.toFixed(1) : p.value} {p.unit}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <MiniSparkline data={device.sparkline} color={healthColor} />
        <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>{device.primaryParamName} (2h)</span>
        <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--text-disabled)' }} />
      </div>
    </div>
  );
}

function AlertsPanel({ alerts, onClose, onAcknowledge }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto shadow-2xl"
        style={{ backgroundColor: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 sticky top-0 z-10" style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <BellRing className="w-4 h-4" style={{ color: '#EF4444' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Active Alerts ({alerts.length})</span>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="p-3 space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className="rounded-lg p-3 text-xs" style={{ backgroundColor: a.severity === 'CRITICAL' ? '#EF444410' : a.severity === 'WARNING' ? '#F59E0B10' : 'var(--hover-bg)', border: `1px solid ${a.severity === 'CRITICAL' ? '#EF444430' : a.severity === 'WARNING' ? '#F59E0B30' : 'var(--border-color)'}` }}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold" style={{ color: a.severity === 'CRITICAL' ? '#EF4444' : a.severity === 'WARNING' ? '#F59E0B' : '#3B82F6' }}>
                  [{a.severity}]
                </span>
                <span style={{ color: 'var(--text-disabled)' }}>{new Date(a.timestamp).toLocaleTimeString()}</span>
              </div>
              <div className="font-medium mb-0.5" style={{ color: 'var(--text-primary)' }}>{a.equipmentCode}</div>
              <div style={{ color: 'var(--text-muted)' }}>{a.parameterName}: <span className="font-mono font-semibold">{a.value} {a.unit}</span> (threshold: {a.threshold})</div>
              {a.status === 'ACTIVE' && (
                <button onClick={() => onAcknowledge(a.alertId)} className="mt-2 text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-primary)' }}>
                  Acknowledge
                </button>
              )}
            </div>
          ))}
          {alerts.length === 0 && <div className="text-center py-8 text-xs" style={{ color: 'var(--text-muted)' }}>No active alerts</div>}
        </div>
      </div>
    </div>
  );
}

export default function IoTHealthMonitor({ projectId }) {
  const [fleetData, setFleetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [allAlerts, setAllAlerts] = useState([]);
  const [simRunning, setSimRunning] = useState(false);
  const wsRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const [notifEnabled, setNotifEnabled] = useState(() => 'Notification' in window && Notification.permission === 'granted');
  const prevAlertCountRef = useRef(0);

  const fetchFleetHealth = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/iot/fleet-health`);
      setFleetData(data);
      if (!data.devices?.length) {
        // Auto-seed
        await api.post(`/v1/projects/${projectId}/iot/seed`);
        const { data: d2 } = await api.get(`/v1/projects/${projectId}/iot/fleet-health`);
        setFleetData(d2);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId]);

  const fetchAlerts = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/iot/alerts/active`);
      const alerts = data.alerts || [];
      setAllAlerts(alerts);
      // Check for new critical/warning alerts and send browser notification
      const critCount = alerts.filter(a => a.severity === 'CRITICAL' || a.severity === 'WARNING').length;
      if (critCount > prevAlertCountRef.current && prevAlertCountRef.current > 0) {
        const newest = alerts.find(a => a.severity === 'CRITICAL') || alerts.find(a => a.severity === 'WARNING');
        if (newest) {
          sendBrowserNotif(
            `[${newest.severity}] ${newest.equipmentCode}`,
            `${newest.parameterName}: ${newest.value} ${newest.unit} (threshold: ${newest.threshold})`,
            `alert-${newest.alertId || Date.now()}`
          );
        }
      }
      prevAlertCountRef.current = critCount;
    } catch {}
  }, [projectId]);

  useEffect(() => {
    fetchFleetHealth();
    fetchAlerts();
  }, [fetchFleetHealth, fetchAlerts]);

  // Auto-refresh every 30s
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => {
      fetchFleetHealth();
      fetchAlerts();
    }, 30000);
    return () => clearInterval(refreshTimerRef.current);
  }, [fetchFleetHealth, fetchAlerts]);

  // WebSocket for live updates + push notifications
  useEffect(() => {
    if (!projectId) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/v1/projects/${projectId}/iot/telemetry/live`);
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'telemetry') {
            // Browser notification for critical health drops
            if (msg.healthScore < 40) {
              sendBrowserNotif(
                `CRITICAL: ${msg.equipmentCode}`,
                `Health score dropped to ${Math.round(msg.healthScore)}/100`,
                `health-${msg.equipmentCode}`
              );
            }
            setFleetData(prev => {
              if (!prev) return prev;
              const devices = prev.devices.map(d => {
                if (d.equipmentCode === msg.equipmentCode) {
                  return { ...d, healthScore: msg.healthScore, status: msg.healthScore < 40 ? 'ALERT' : d.status };
                }
                return d;
              });
              return { ...prev, devices };
            });
          } else if (msg.type === 'alert' && (msg.severity === 'CRITICAL' || msg.severity === 'WARNING')) {
            sendBrowserNotif(
              `[${msg.severity}] ${msg.equipmentCode}`,
              `${msg.parameterName}: ${msg.value} ${msg.unit}`,
              `ws-alert-${Date.now()}`
            );
            fetchAlerts();
          }
        } catch {}
      };
      wsRef.current = ws;
    } catch {}
    return () => { if (ws) ws.close(); };
  }, [projectId, fetchAlerts]);

  const handleStartSim = async () => {
    try {
      await api.post(`/v1/projects/${projectId}/iot/simulator/start`);
      setSimRunning(true);
      toast.success('Simulator started');
    } catch { toast.error('Failed to start simulator'); }
  };

  const handleStopSim = async () => {
    try {
      await api.post(`/v1/projects/${projectId}/iot/simulator/stop`);
      setSimRunning(false);
      toast.success('Simulator stopped');
    } catch {}
  };

  const handleTick = async () => {
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/iot/simulator/tick`);
      toast.success(`Generated telemetry: ${data.generated} devices, ${data.newAlerts} new alerts`);
      if (data.newAlerts > 0) {
        sendBrowserNotif('IoT Alert', `${data.newAlerts} new alert(s) detected across fleet`, 'tick-alerts');
      }
      fetchFleetHealth();
      fetchAlerts();
    } catch { toast.error('Tick failed'); }
  };

  const handleEnableNotifications = () => {
    if ('Notification' in window) {
      Notification.requestPermission().then(p => {
        setNotifEnabled(p === 'granted');
        if (p === 'granted') toast.success('Push notifications enabled');
        else toast.error('Notifications blocked by browser');
      });
    } else {
      toast.error('Browser does not support notifications');
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.post(`/v1/projects/${projectId}/iot/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      fetchAlerts();
      fetchFleetHealth();
    } catch {}
  };

  const devices = fleetData?.devices || [];
  const summary = fleetData?.summary || {};

  if (selectedDevice) {
    return (
      <EquipmentDetailPanel
        projectId={projectId}
        equipmentCode={selectedDevice.equipmentCode}
        device={selectedDevice}
        onBack={() => { setSelectedDevice(null); fetchFleetHealth(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="iot-health-monitor">
      {/* Control Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchFleetHealth} data-testid="iot-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleTick} data-testid="iot-tick-btn">
            <Zap className="w-3.5 h-3.5 mr-1" /> Generate Tick
          </Button>
          {!simRunning ? (
            <Button variant="outline" size="sm" onClick={handleStartSim} data-testid="iot-start-sim-btn">
              <Play className="w-3.5 h-3.5 mr-1" /> Start Simulator
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleStopSim} data-testid="iot-stop-sim-btn">
              <Square className="w-3.5 h-3.5 mr-1" /> Stop Simulator
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchAlerts(); setShowAlerts(true); }} data-testid="iot-alerts-btn">
            <BellRing className="w-3.5 h-3.5 mr-1" style={{ color: summary.activeAlerts > 0 ? '#EF4444' : undefined }} />
            Alerts ({summary.activeAlerts || 0})
          </Button>
          {!notifEnabled && (
            <Button variant="outline" size="sm" onClick={handleEnableNotifications} data-testid="iot-enable-notif-btn">
              <Bell className="w-3.5 h-3.5 mr-1" /> Enable Notifications
            </Button>
          )}
          {notifEnabled && (
            <span className="text-[10px] px-2 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: '#22C55E15', color: '#22C55E' }}>
              <Bell className="w-3 h-3" /> Push ON
            </span>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <FleetKPIStrip summary={summary} />

      {/* Equipment Health Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3" data-testid="iot-equipment-grid">
          {devices.map(d => (
            <EquipmentCard key={d.equipmentCode} device={d} onClick={() => setSelectedDevice(d)} />
          ))}
        </div>
      )}

      {/* Alerts Panel */}
      {showAlerts && <AlertsPanel alerts={allAlerts} onClose={() => setShowAlerts(false)} onAcknowledge={handleAcknowledge} />}
    </div>
  );
}
